// src/services/timelineAdmin.js
// Utilitários de ADMIN para listar/filtrar posts e replies (comentários) da timeline.

import { db } from "../firebase";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy,
  limit as qlimit,
  startAfter,
} from "firebase/firestore";
import {
  setPostStatus,
  hardDeletePost,
  softDeletePost,
  blockUser,
  unblockUser,
  setReplyStatus,
  softDeleteReply,
} from "./timelineService";

const FEED_COLL = "timelineChunks";

function toDateSafe(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") return v.toDate();
  if (typeof v === "number") return new Date(v);
  return new Date(v);
}

/**
 * Lista posts da timeline “achatando” os chunks.
 * Filtros:
 *  - status: "all" | "pending" | "published" | "rejected" | "deleted"
 *  - text: filtro textual (autor, texto, categoria) — client-side
 * Paginação:
 *  - cursor: último doc de chunk (snapshot) — mantido pelo chamador
 */
export async function adminFetchPosts({
  status = "all",
  text = "",
  limitChunks = 4,
  cursor = null,
} = {}) {
  let q = query(collection(db, FEED_COLL), orderBy("index", "desc"), qlimit(limitChunks));
  if (cursor) {
    q = query(collection(db, FEED_COLL), orderBy("index", "desc"), startAfter(cursor), qlimit(limitChunks));
  }

  const snap = await getDocs(q);
  const chunks = snap.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));
  const nextCursor = chunks.length ? chunks[chunks.length - 1]._doc : null;

  let flat = [];
  for (const ch of chunks) {
    const posts = Array.isArray(ch.posts) ? ch.posts : [];
    for (const p of posts) {
      if (!p?.postId) continue;
      const createdAtDate = toDateSafe(p.createdAt ?? p.createdAtMs);
      flat.push({
        ...p,
        chunkId: ch.id,
        chunkIndex: ch.index ?? 0,
        createdAtDate,
        createdAtMs: createdAtDate ? createdAtDate.getTime() : 0, // << importante p/ ordenar com replies
        isReply: false,
        _doc: ch._doc,
      });
    }
  }

  if (status !== "all") flat = flat.filter((p) => String(p.status) === status);

  const s = text.trim().toLowerCase();
  if (s) {
    flat = flat.filter((p) => {
      const a = (p.authorName || "").toLowerCase();
      const t = (p.text || "").toLowerCase();
      const c = (p.attach?.categoria || "").toLowerCase();
      return a.includes(s) || t.includes(s) || c.includes(s);
    });
  }

  // ordena do mais novo pro mais velho
  flat.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));

  return { items: flat, nextCursor };
}

/**
 * Busca replies (comentários em posts) para moderação via Collection Group.
 * Filtros:
 *  - status: "all" | "pending" | "published" | "rejected" | "deleted"
 *  - text: filtro textual (autor + texto) — client-side
 * Paginação:
 *  - cursor: último doc snapshot retornado (use o nextCursor devolvido)
 * Requer índice: (status ASC, createdAt DESC, __name__ DESC)
 */
export async function adminFetchReplies({
  status = "all",
  text = "",
  cursor = null,
  pageSize = 50,
} = {}) {
  const constraints = [orderBy("createdAt", "desc"), qlimit(pageSize)];
  if (status !== "all") constraints.unshift(where("status", "==", status));
  if (cursor) constraints.push(startAfter(cursor));

  const q = query(collectionGroup(db, "replies"), ...constraints);
  const snap = await getDocs(q);

  let items = snap.docs.map((d) => {
    const data = d.data() || {};
    const parentPostId = d.ref?.parent?.parent?.id || data.postId || null;
    const createdAtDate = toDateSafe(data.createdAt);
    return {
      replyId: d.id,
      parentPostId,
      postId: parentPostId,
      text: data.text || "",
      status: data.status || "pending",
      deleted: !!data.deleted,
      authorUid: data.authorUid || null,
      authorName: data.authorName || "Usuário",
      authorAvatar: data.authorAvatar || "",
      especialista: !!data.especialista,
      createdAt: data.createdAt || null,
      createdAtDate,
      createdAtMs: createdAtDate ? createdAtDate.getTime() : 0,
      isReply: true,
      _doc: d, // snapshot p/ paginação
    };
  });

  const s = text.trim().toLowerCase();
  if (s) {
    items = items.filter((r) => {
      const a = (r.authorName || "").toLowerCase();
      const t = (r.text || "").toLowerCase();
      return a.includes(s) || t.includes(s);
    });
  }

  const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items, nextCursor };
}

/* ===== ações admin (posts) ===== */
export async function adminApprove(postId)    { await setPostStatus(postId, "published"); }
export async function adminReject(postId)     { await setPostStatus(postId, "rejected"); }
export async function adminSoftDelete(postId) { await softDeletePost(postId); }
export async function adminHardDelete(postId) { await hardDeletePost(postId); }
export async function adminBlockUser(uid)     { await blockUser(uid); }
export async function adminUnblockUser(uid)   { await unblockUser(uid); }

/* ===== ações admin (replies) ===== */
export async function adminApproveReply(postId, replyId) { await setReplyStatus({ postId, replyId, status: "published" }); }
export async function adminRejectReply(postId, replyId)  { await setReplyStatus({ postId, replyId, status: "rejected"  }); }
export async function adminSoftDeleteReply(postId, replyId) { await softDeleteReply({ postId, replyId }); }
